import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import * as api from "../../api/client";
import { ApiError } from "../../api/client";
import type { ServerOutletContext } from "../ServerLayout";
import { CopyButton } from "../../components/CopyButton";
import { CommandBar } from "../../components/CommandBar";
import { formatDateTime, formatRelativeTime } from "../../utils/format";

type PendingAction = "authenticate" | "invalidate" | "rotate" | null;

export function StatusTab() {
  const { detail, refresh } = useOutletContext<ServerOutletContext>();
  const { server, credential, heartbeat } = detail;

  const [pending, setPending] = useState<PendingAction>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [revealedCredential, setRevealedCredential] = useState<string | null>(null);

  async function runAction(action: PendingAction, confirmMessage: string | null, fn: () => Promise<{ credential?: string } | unknown>) {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    setPending(action);
    setActionError(null);
    setRevealedCredential(null);
    try {
      const result = await fn();
      if (result && typeof result === "object" && "credential" in result && typeof (result as { credential?: string }).credential === "string") {
        setRevealedCredential((result as { credential: string }).credential);
      }
      refresh();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "The action could not be completed.");
    } finally {
      setPending(null);
    }
  }

  return (
    <div className="stack">
      {actionError && <div className="form-error">{actionError}</div>}

      {revealedCredential && (
        <div className="credential-reveal">
          <strong>New credential issued</strong>
          <p style={{ margin: "6px 0 10px", fontSize: "13px" }}>
            This is shown once and is not retrievable again. Paste it into the generated Roblox client, or
            re-generate the client from the Generator page instead.
          </p>
          <div className="row">
            <code className="mono" style={{ wordBreak: "break-all" }}>
              {revealedCredential}
            </code>
            <CopyButton text={revealedCredential} />
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel-header">
          <h2>Server details</h2>
        </div>
        <div className="field-grid">
          <div>
            <span className="field-label">Server ID</span>
            <div className="field-value mono">{server.id}</div>
          </div>
          <div>
            <span className="field-label">Client version</span>
            <div className="field-value mono">{server.client_version ?? "—"}</div>
          </div>
          <div>
            <span className="field-label">Generator version</span>
            <div className="field-value mono">{server.generator_version ?? "—"}</div>
          </div>
          <div>
            <span className="field-label">Registered</span>
            <div className="field-value">{formatDateTime(server.created_at)}</div>
          </div>
          <div>
            <span className="field-label">Last communication</span>
            <div className="field-value">{formatDateTime(server.updated_at)}</div>
          </div>
          <div>
            <span className="field-label">Last heartbeat</span>
            <div className="field-value">{formatRelativeTime(server.last_heartbeat_at)}</div>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h2>Credential status</h2>
        </div>
        {credential ? (
          <div className="field-grid">
            <div>
              <span className="field-label">Status</span>
              <div className="field-value mono">{credential.status}</div>
            </div>
            <div>
              <span className="field-label">Issued</span>
              <div className="field-value">{formatDateTime(credential.createdAt)}</div>
            </div>
            <div>
              <span className="field-label">Expires</span>
              <div className="field-value">{formatDateTime(credential.expiresAt)}</div>
            </div>
            <div>
              <span className="field-label">Last used</span>
              <div className="field-value">{formatRelativeTime(credential.lastUsedAt)}</div>
            </div>
          </div>
        ) : (
          <p className="muted">No active credential. Use "Authenticate" to issue one.</p>
        )}

        <div className="btn-row" style={{ marginTop: "var(--space-4)" }}>
          <button
            type="button"
            className="btn"
            disabled={pending !== null}
            onClick={() => runAction("authenticate", null, () => api.authenticateServer(server.id))}
          >
            {pending === "authenticate" ? "Authenticating…" : "Authenticate"}
          </button>
          <button
            type="button"
            className="btn"
            disabled={pending !== null}
            onClick={() => runAction("rotate", "Rotate this server's credential? The current credential will stop working immediately.", () => api.rotateCredential(server.id))}
          >
            {pending === "rotate" ? "Rotating…" : "Rotate Credential"}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={pending !== null}
            onClick={() => runAction("invalidate", "Invalidate authentication for this server? It will be unable to communicate until re-authenticated.", () => api.invalidateAuthentication(server.id))}
          >
            {pending === "invalidate" ? "Invalidating…" : "Invalidate Authentication"}
          </button>
        </div>
      </div>

      <div className="panel">
        <div className="panel-header">
          <h2>Latest heartbeat</h2>
        </div>
        {heartbeat ? (
          <div className="field-grid">
            <div>
              <span className="field-label">Health state</span>
              <div className="field-value mono">{heartbeat.health_state}</div>
            </div>
            <div>
              <span className="field-label">Last seen</span>
              <div className="field-value">{formatRelativeTime(heartbeat.last_seen_at)}</div>
            </div>
            <div>
              <span className="field-label">Reported client version</span>
              <div className="field-value mono">{heartbeat.client_version ?? "—"}</div>
            </div>
          </div>
        ) : (
          <p className="muted">This server has not sent a heartbeat yet.</p>
        )}
      </div>

      <CommandBar
        serverId={server.id}
        disabled={server.authentication_state === "invalidated" || server.authentication_state === "revoked"}
      />
    </div>
  );
}
